import App from "../App";
import { Howl } from "howler";
import { Tween } from "gsap";
import LocalStorageVars from "../../src/const/LocalStorageVars";
import LocalData from "./LocalData";
import GameModel from "../../src/model/GameModel";
import { TweenMax } from "gsap";
import SoundVars from "../const/SoundVars";
import GameEvents from "../const/GameEvents";
import ConsoleUtil from "./ConsoleUtil";
import GameLoader from './GameLoader';

/**
 * Provides methods for working with sound and music.
 */
class SoundController {
    protected soundBlobs: Map<number | string, ISoundBlob>;
    protected defaultMusicFadeTime: number;
    protected musicFadeTime: number;
    protected soundEnabled: boolean;
    protected musicEnabled: boolean;
    protected effectsEnabled: boolean;
    protected playingMusic: boolean;
    protected musicVol: number;
    protected masterVol: number;
    protected effectsVol: number;
    protected music: GameHowl;
    protected currentMusic: number | string;
    protected startMusicCall: Tween;
    private backgroundMusics: { [id: string]: GameHowl };
    public musicRequested: boolean = false;
    public howler: HowlerGlobal;
    public soundsLoaded: boolean = false;
    public currentSounds: GameHowl[] = [];

    private warningsShown: Map<string, boolean> = new Map<string, boolean>();

    constructor() {
        this.backgroundMusics = {};
    }

    public init() {
        this.soundBlobs = new Map<number | string, ISoundBlob>();
        this.defaultMusicFadeTime =
            GameModel.gameConfig.defaultMusicFadeTime || 0.3;
        // this.soundEnabled = LocalData.getBooleanValue(LocalStorageVars.SOUND_ENABLED);
        this.soundEnabled = true;
        this.musicEnabled = true;
        // this.musicEnabled = LocalData.getBooleanValue(LocalStorageVars.MUSIC_ENABLED);
        this.effectsEnabled = true;
        // this.effectsEnabled = LocalData.getBooleanValue(LocalStorageVars.EFFECTS_ENABLED);
        // this.masterVol = LocalData.getNumber(LocalStorageVars.MASTER_VOLUME) || 1; // Why was it removed ????
        // this.masterVol = 1;
        // this.musicVol = LocalData.getNumber(LocalStorageVars.MUSIC_VOLUME) || 1;
        this.musicVol = 1;
        // this.effectsVol = LocalData.getNumber(LocalStorageVars.EFFECTS_VOLUME) || 1;
        this.effectsVol = 1;
        this.howler = Howler;

        let interv = setInterval(() => {
            if ((Howler as any)._audioUnlocked) {
                ConsoleUtil.log("audio unlocked");
                clearInterval(interv);
            }
        }, 50);
        this.toggleVisibilitychangeEvent();
    }

    /**
     * Switched on or off game music when game tab is lose focus
     */
    private toggleMusicWhenVisibilityChange = (): void => {
        if (document.visibilityState == "hidden") {
            this.howler.mute(true);
        } else {
            this.howler.mute( this.isEffectsEnabled || this.isMusicEnabled ? false : true);
        }
    }

    /**
     * Add or remove document "visibilitychange" event for switch on or off music when game tab is lose focus
     * @param add - if not set param, default value is *"true"* (add listener)
     */
    public toggleVisibilitychangeEvent(add: boolean = true): void {
        if (add) {
            document.addEventListener("visibilitychange", this.toggleMusicWhenVisibilityChange);
        } else {
            document.removeEventListener("visibilitychange", this.toggleMusicWhenVisibilityChange);
        }
    }

    public toggleMasterSound(state?: any) {
        if (this.isEffectsEnabled && this.isMusicEnabled) {
            this.toggleEffects();
            this.toggleMusic();
            this.toggleSound(false);
        } else if (!this.isEffectsEnabled && this.isMusicEnabled) {
            this.toggleMusic();
            this.toggleSound(false);
        } else if (this.isEffectsEnabled && !this.isMusicEnabled) {
            this.toggleEffects();
            this.toggleSound(false);
        } else if (!this.isEffectsEnabled && !this.isMusicEnabled) {
            this.toggleEffects();
            this.toggleMusic();
            this.toggleSound(true);
     }
}

    /**
     * Global sound toggle
     * @param state
     */
    public toggleSound(state?: boolean) {
        this.soundEnabled = state != undefined ? state : !this.soundEnabled;
        ConsoleUtil.log("[SoundController] Toggle Sound: " + this.soundEnabled);
        this.howler.mute(!this.soundEnabled);
        LocalData.saveValue(LocalStorageVars.SOUND_ENABLED, this.soundEnabled);
        if (this.soundEnabled) {
            this.masterVolume =
                LocalData.getNumber(LocalStorageVars.MASTER_VOLUME) || 1;
            App.dispatcher.emit(GameEvents.SOUNDS_UNMUTED);
        } else {
            this.masterVolume = 0;
            App.dispatcher.emit(GameEvents.SOUNDS_MUTED);
        }
        return this.soundEnabled;
    }

    /**
     * Music sound toggle. If soundEnabled is set to false. This will have no effect
     */
    public toggleMusic(state?: boolean) {
        this.musicEnabled = state || !this.musicEnabled;
        ConsoleUtil.log("[SoundController] Toggle Music: " + this.musicEnabled);
        LocalData.saveValue(LocalStorageVars.MUSIC_ENABLED, this.musicEnabled);
        if (this.music) {
            if (this.musicEnabled) {
                if (!this.music.playing()) {
                    this.music.play();
                }
            } else {
                this.music.pause();
            }
        }
        this.toggleSound(this.musicEnabled || this.effectsEnabled);
        return this.musicEnabled;
    }

    public fadeMusic(from: number, to: number, duration: number) {
        if (this.music) {
            this.music.fade(from, to, duration * 1000);
        }
    }

    public toggleEffects(state?: boolean) {
        this.effectsEnabled = state || !this.effectsEnabled;
        ConsoleUtil.log(
            "[SoundController] Toggle Effects: " + this.effectsEnabled
        );
        if (this.effectsEnabled) {
            this.effectsVol = 1;
        } else {
            this.effectsVol = 0;
        }
        LocalData.saveValue(
            LocalStorageVars.EFFECTS_ENABLED,
            this.effectsEnabled
        );
        for (let i = 0; i < this.currentSounds.length; i++) {
            if (this.currentSounds[i] != this.music) {
                this.currentSounds[i].mute(!this.effectsEnabled);
            }
        }
        this.toggleSound(this.musicEnabled || this.effectsEnabled);
    }

    get isSoundEnabled() {
        return this.soundEnabled;
    }

    get isMusicEnabled() {
        return this.musicEnabled;
    }

    get isEffectsEnabled() {
        return this.effectsEnabled;
    }

    get masterVolume() {
        if (
            this.masterVol === undefined ||
            this.masterVol === null ||
            isNaN(this.masterVol)
        ) {
            this.masterVol = this.soundEnabled ? 1 : 0;
        }
        return this.masterVol;
    }

    get effectsVolume() {
        return this.effectsVol;
    }

    get musicVolume() {
        return this.musicVol;
    }

    set masterVolume(volume: number) {
        if (this.masterVol === volume) {
            return;
        }
        this.masterVol = volume;
        LocalData.saveValue(LocalStorageVars.MASTER_VOLUME, volume);
        this.howler.volume(this.masterVol);

        let soundEnabled = false;
        if (this.masterVolume > 0) {
            soundEnabled = true;
        } else {
            soundEnabled = false;
        }
        LocalData.saveValue(LocalStorageVars.MASTER_VOLUME, this.masterVol);
        if (soundEnabled != this.soundEnabled) {
            this.toggleMasterSound({ data: { value: soundEnabled } });
            this.howler.mute(!this.soundEnabled);
        }
    }

    set effectsVolume(volume: number) {
        this.effectsVol = volume;
        LocalData.saveValue(LocalStorageVars.EFFECTS_VOLUME, volume);
        for (let i = 0; i < this.currentSounds.length; i++) {
            const currentSound = this.currentSounds[i];
            currentSound.volume(this.effectsVol);
        }
    }

    set musicVolume(volume: number) {
        this.musicVol = volume;
        LocalData.saveValue(LocalStorageVars.MUSIC_VOLUME, volume);
        if (this.music) {
            this.music.volume(this.musicVol);
        }
    }

    public playSound(
        sndId: string,
        loop = false,
        sprites?: IHowlSoundSpriteDefinition,
        alowDuplicate = true
    ) {
        if (!sndId) {
            return;
        }

        if (!alowDuplicate && this.soundIsPlaying(sndId)) {
            return;
        }

        const snd: GameHowl = this.getSound(
            sndId,
            loop,
            this.effectsVol,
            sprites
        );
        ConsoleUtil.log("[SoundController] Play sound: " + sndId);
        if (this.soundEnabled) {
            let volumeOverride = App.config.soundVolumeOverride?.[sndId];
            if (volumeOverride) {
                ConsoleUtil.log(
                    "[SoundController] Override volume:",
                    volumeOverride
                );
                snd.volume(volumeOverride);
            }

            snd.play();
            if (loop == false) {
                snd.once("end", () => {
                    this.unloadSound(snd);
                });
            }
        }

        return snd;
    }

    public stopSound(sound: Howl) {
        sound.stop();
        sound.off();
        this.unloadSound(sound);
    }

    public fadeStop(sound: Howl, duration) {
        this.fadeSound(sound, sound.volume(), 0, duration, () => {
            this.stopSound(sound);
        });
    }

    public fadeSound(
        sound: Howl,
        from: number,
        to: number,
        duration: number,
        onComplete?: () => void
    ) {
        if (onComplete) {
            sound.on("fade", () => {
                onComplete();
            });
        }
        sound.fade(from, to, duration * 1000);
        sound.on;
    }

    /**
     * Play music
     * @param musicId
     * @param seek
     * @param seamless - if added uses gameconfig's musicTempTime to switch the sound at the start of the beat
     */
    playMusic(musicId: number | string, seek: number = -1, seamless = false) {
        if (this.music) {
            if (this.currentMusic !== musicId) {
                if (seamless) {
                    const seekTime = this.music.seek() as number;
                    const delay = seekTime % GameModel.gameConfig.musicTempTime;
                    this.music.fade(1, 0, delay * 1000);
                    switch (musicId) {
                        case SoundVars.MUSIC_MAIN:
                            this.playSound(SoundVars.EFFECT_SWITCH_TO_MAIN);
                            break;
                        case SoundVars.MUSIC_MAIN2:
                        case SoundVars.MUSIC_MAIN3:
                        case SoundVars.MUSIC_BONUS:
                            this.playSound(SoundVars.EFFECT_SWITCH_TO_NEXT);
                            break;
                    }
                    TweenMax.delayedCall(
                        delay,
                        () => {
                            this.stopMusic();
                            this.startMusic(musicId);
                            this.music.seek(0);
                            this.music.fade(0, this.musicVol, 200);
                        },
                        null,
                        this
                    );
                } else {
                    this.stopMusic();
                    this.startMusic(musicId);
                    if (seek != -1) {
                        this.music.seek(seek);
                    }
                    this.music.fade(0, this.musicVol, 1);
                }
            }
        } else {
            this.startMusic(musicId);
        }
    }

    public startMusic(musicId: string | number): void {
        if (this.backgroundMusics[musicId] == undefined) {
            this.backgroundMusics[musicId] = this.getSound(
                musicId,
                true,
                this.musicVol
            );
        }
        // if (this.backgroundMusics[musicId] =)

        this.music = this.backgroundMusics[musicId];
        // this.music.once("playerror", () => {
        //     this.music.once('unlock', () => {
        //         this.music.play();
        //         this.music.off("playrror");
        //     });
        // })

        if (this.musicEnabled) {
            this.music.play();
            // this.music.fade(0, this.musicVol, 1);
        }

        this.playingMusic = true;
        this.currentMusic = musicId;
    }

    public stopMusic(): void {
        if (this.music) {
            this.music.pause();
        }

        this.playingMusic = false;
    }

    public resumeMusic(): void {
        if (this.music) {
            this.music.play();
            this.playingMusic = true;
        }
    }

    get isMusicPlaying() {
        return this.playingMusic && this.music.playing() && this.music.volume();
    }

    public soundIsPlaying(soundId: string | number): boolean {
        let soundPlaying = false;
        if (this.currentSounds) {
            this.currentSounds.forEach((snd) => {
                if (snd.name === soundId) {
                    soundPlaying = true;
                }
            });
        }

        return soundPlaying;
    }

    /**
     * Get a raw sound
     * @param soundId
     * @param loop
     * @param volume
     * @param sprites
     * @returns {*|GameHowl}
     */
    protected getSound(
        soundId: string | number,
        loop = false,
        volume = 1,
        sprites?: IHowlSoundSpriteDefinition
    ): GameHowl {
        let blobUrl: string | ISoundBlob | undefined =
            this.soundBlobs.get(soundId);

        if (blobUrl == undefined) {
            if (!soundId || !this.warningsShown.get(soundId.toString())) {
                console.warn(
                    "Could not find data for sound with id : " + soundId
                );
                this.warningsShown.set(soundId ? soundId.toString() : "", true);
            }

            const sound = new GameHowl({
                src: [""],
                format: ["m4a"],
                loop,
                preload: true,
                volume,
                sprite: sprites ? sprites : undefined,
                name: soundId,
            });

            if (this.soundBlobs.has(soundId) != undefined) {
                this.currentSounds.push(sound);
            }

            return sound;
        }

        const soundBlob = this.soundBlobs.get(soundId);

        const snd = new GameHowl({
            src: [soundBlob.url],
            format: [soundBlob.extension],
            loop,
            preload: true,
            volume,
            sprite: sprites ? sprites : undefined,
            name: soundId,
        });

        if (this.soundBlobs.has(soundId)) {
            this.currentSounds.push(snd);
        }
        return snd;
    }

    public loadSounds(soundList: string[][]) {
        for (let j = 0; j < soundList.length; j++) {
            const sndId = soundList[j][0];
            let blobUrl: string | ISoundBlob | undefined =
                this.soundBlobs.get(sndId);
            if (blobUrl == undefined) {
                // GameLoader.loader.resources
                // PIXI.loader.resources
                const byteArray = PIXI.loader.resources[sndId];
                const format = byteArray.data.type.split("/")[1];
                if (byteArray) {
                    const blob = new Blob([byteArray.data], {
                        type: byteArray.data.type,
                    });
                    blobUrl = window.URL.createObjectURL(blob);
                    const soundBlob: ISoundBlob = {
                        url: blobUrl,
                        extension: format,
                    };

                    this.soundBlobs.set(sndId, soundBlob);
                    const snd = new GameHowl({
                        src: [soundBlob.url],
                        format: [soundBlob.extension],
                        preload: true,
                        name: sndId,
                    });
                }
            }
        }
        this.soundsLoaded = true;
    }

    public unloadSound(snd: Howl) {
        const index = this.currentSounds.indexOf(snd as GameHowl);
        if (index > -1) {
            this.currentSounds.splice(index, 1);
        }

        snd.unload();
    }
}

interface ISoundBlob {
    url: string;
    extension: string;
}

export class GameHowl extends Howl {
    public name: string | number;
    constructor(props: IGameHowlProperties) {
        super(props);
        this.name = props.name;
    }
}

interface IGameHowlProperties extends IHowlProperties {
    name: string | number;
}

export default new SoundController();
